import { useState, useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { contrastRatio } from '@/lib/utils';

export function ContrastChecker() {
  const [fg, setFg] = useState('#0f172a');
  const [bg, setBg] = useState('#f8fafc');

  const ratio = useMemo(() => contrastRatio(fg, bg), [fg, bg]);

  const checks = [
    { name: 'AA — Normal text', threshold: 4.5, hint: 'WCAG 2.1 AA' },
    { name: 'AA — Large text', threshold: 3, hint: '18pt+ or 14pt bold' },
    { name: 'AAA — Normal', threshold: 7, hint: 'Highest standard' },
    { name: 'AAA — Large', threshold: 4.5, hint: 'Large + AAA' },
    { name: 'UI components', threshold: 3, hint: 'Borders, icons' },
  ];

  return (
    <div>
      <ToolHeader
        toolId="contrast-checker"
        title="Contrast checker"
        description="WCAG 2.1 contrast ratio between two colors. Test combinations for accessibility compliance."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Field label="Foreground">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={fg}
                  onChange={(e) => setFg(e.target.value)}
                  className="!w-12 !h-9"
                />
                <Input value={fg} onChange={(e) => setFg(e.target.value)} className="font-mono" />
              </div>
            </Field>
            <Field label="Background">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                  className="!w-12 !h-9"
                />
                <Input value={bg} onChange={(e) => setBg(e.target.value)} className="font-mono" />
              </div>
            </Field>
          </div>
          <div className="text-center py-6 mt-2 rounded-xl border border-border" style={{ background: bg, color: fg }}>
            <div className="text-2xl font-semibold">The quick brown fox</div>
            <div className="text-sm mt-2 opacity-90">jumps over the lazy dog</div>
            <div className="text-xs mt-3 opacity-75">14pt sample</div>
          </div>
        </Card>

        <Card className="flex flex-col items-center justify-center text-center">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">Contrast ratio</div>
          <div className="text-6xl font-mono font-semibold mt-2 tabular-nums">
            {ratio.toFixed(2)}
            <span className="text-2xl text-fg-subtle">:1</span>
          </div>
          <div className="mt-4">
            <Badge variant={ratio >= 7 ? 'success' : ratio >= 4.5 ? 'success' : ratio >= 3 ? 'warning' : 'danger'}>
              {ratio >= 7
                ? 'AAA · Excellent'
                : ratio >= 4.5
                ? 'AA · Pass'
                : ratio >= 3
                ? 'AA Large · Pass'
                : 'Fails WCAG'}
            </Badge>
          </div>
        </Card>
      </div>

      <Card>
        <div className="text-xs font-medium text-fg-muted mb-3">WCAG checks</div>
        <div className="space-y-2">
          {checks.map((c) => {
            const pass = ratio >= c.threshold;
            return (
              <div key={c.name} className="flex items-center justify-between p-3 rounded-lg bg-bg-inset border border-border">
                <div>
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-fg-subtle">
                    {c.hint} · requires ≥ {c.threshold}:1
                  </div>
                </div>
                <div
                  className={`size-7 rounded-full flex items-center justify-center ${
                    pass ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger'
                  }`}
                >
                  {pass ? <Check size={14} /> : <X size={14} />}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export default ContrastChecker;
